import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Grid from '@material-ui/core/Grid';
import IconButton from '@material-ui/core/IconButton';
import copy from 'copy-to-clipboard';
import { isFarmBasedPolicyType } from '@ourbranch/farm-utils';

import { useRouteHelper } from 'core/hooks/use-route-helper';
import { useToast } from 'core/components/toast';
import { useStore } from 'core/store';
import { Label } from 'core/components/label';
import { IconButtonWithTooltip } from 'core/components/icon-button-with-tooltip';
import copyIcon from 'core/assets/svg/copy.svg';
import navigateIcon from 'core/assets/svg/ic-navigate.svg';
import backIcon from './go-to.svg';
import FarmNavigator from 'core/components/farm-navigator';
import useStyles from './screen-header.styles';

function ScreenHeader({ title, subtitles, children, onBack, rightValue, rightLink, options, className }) {
  const classes = useStyles();
  const hasElementsOnRight = rightValue || children;
  const toast = useToast();
  const { offer: offerStore } = useStore();

  const { inOfferEditRoute } = useRouteHelper();
  const showFarmNavigator = isFarmBasedPolicyType(offerStore?.selectedOption) && inOfferEditRoute;
  const [isNavigatorOpen, setIsNavigatorOpen] = useState(false);
  const containerRef = useRef(null);
  const buttonContainerRef = useRef(null);

  useEffect(() => {
    const updatePosition = () => {
      if (containerRef.current && buttonContainerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        buttonContainerRef.current.style.transform = `translateX(${rect.left - 64}px)`;
      }
    };

    let timeoutId = null;
    const debouncedUpdatePosition = () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      timeoutId = setTimeout(updatePosition, 30);
    };

    updatePosition(); // Initial call
    window.addEventListener('resize', debouncedUpdatePosition);

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      window.removeEventListener('resize', debouncedUpdatePosition);
    };
  }, []);

  return (
    <>
      {showFarmNavigator && (
        <FarmNavigator
          isNavigatorOpen={isNavigatorOpen}
          closeNavigator={() => setIsNavigatorOpen(false)}
          location="offer"
        />
      )}
      <Grid container className={classNames(classes.header, className)} justifyContent="space-between">
        <Grid item className={classes.container} ref={containerRef}>
          <div className={classes.fixedButtonContainer} ref={buttonContainerRef}>
            {!options.hideBackButton && (
              <IconButtonWithTooltip
                tooltip="Go back to previous page"
                icon={backIcon}
                onClick={onBack}
                iconClassName={classes.icon}
                imgClassName={classes.iconImg}
                placement="right"
              />
            )}
            {showFarmNavigator && (
              <IconButtonWithTooltip
                tooltip="Navigate this offer"
                icon={navigateIcon}
                onClick={() => setIsNavigatorOpen(true)}
                iconClassName={classes.icon}
                imgClassName={classes.navigatorIconImg}
                placement="right"
              />
            )}
          </div>
          {subtitles ? (
            <Grid container direction="column">
              <Label type="title">{title}</Label>
              {subtitles}
            </Grid>
          ) : (
            <Grid container direction="row">
              <Label type="title">{title}</Label>
              {options.copyableTitle && !title?.toLowerCase().includes('loading') && (
                <IconButton
                  onClick={() => {
                    const copied = copy(title);
                    if (copied) {
                      toast.notify({
                        type: 'success',
                        message: `${title} copied to clipboard`
                      });
                    }
                  }}
                >
                  <img src={copyIcon} alt="copy" />
                </IconButton>
              )}
            </Grid>
          )}
        </Grid>
        {hasElementsOnRight && (
          <Grid item className={classes.rightContainer}>
            {rightValue && (
              <Label type="infoValue" className={classes.rightValue}>
                {rightLink ? (
                  <a className={classes.link} href={rightLink} target="_blank" rel="noopener noreferrer">
                    {rightValue}
                  </a>
                ) : (
                  rightValue
                )}
              </Label>
            )}
            {children}
          </Grid>
        )}
      </Grid>
    </>
  );
}

ScreenHeader.propTypes = {
  title: PropTypes.node.isRequired,
  subtitles: PropTypes.node,
  onBack: PropTypes.func,
  rightValue: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  children: PropTypes.node,
  rightLink: PropTypes.string,
  options: PropTypes.object,
  className: PropTypes.string
};

ScreenHeader.defaultProps = {
  children: null,
  rightValue: null,
  rightLink: undefined,
  subtitles: undefined,
  className: '',
  options: { hideBackButton: false, copyableTitle: false },
  onBack: null
};

export default ScreenHeader;
